import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState } from "react"
import { formatISODate } from "../../../utils/datetime"
import { useDistinctPatientIssuers } from "../../../hooks/useReportRoutingLogs"
import Loader from "../../../components/Loader"
import Pagination from "../../../components/Pagination"
import Filter from "../../../components/TableFilters"
import ReportRoutingModal from "./ReportRoutingModal"

const columnHelper = createColumnHelper()

const ReportRoutingLogsTable = (props) => {
  const {
    data,
    loading,
    onPaginationChange,
    pagination,
    pageCount,
    sorting,
    setSorting,
    filters,
    setFilters,
  } = props

  const { data: distinctIssuers } = useDistinctPatientIssuers()

  const columns = useMemo(
    () => [
      columnHelper.accessor("AccessionNo", {
        header: "Accession Number",
        id: "AccessionNo",
        size: 200,
        cell: (info) => (
          <span
            className="font-medium text-blue-600 hover:underline cursor-pointer"
            onClick={() =>
              showModal(
                info.row.original.ReportRoutingId,
                info.row.original.ReRouteReason === "NORMAL_REPORT"
              )
            }
          >
            {info.renderValue() || ""}
          </span>
        ),
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("PatientIssuer", {
        header: "Issuer Name",
        id: "PatientIssuer",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options:
            distinctIssuers?.length > 0 ? ["All", ...distinctIssuers] : ["All"],
        },
      }),
      columnHelper.accessor("ReRouteReason", {
        header: "Event",
        id: "ReRouteReason",
        size: 200,
        cell: (info) => info.renderValue() || "-",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("Description", {
        header: "Keywords Matched",
        id: "Description",
        size: 200,
        cell: (info) => info.renderValue() || "-",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("createdAt", {
        header: "Date Created",
        size: 200,
        id: "createdAt",
        cell: (info) =>
          info.renderValue() ? formatISODate(info.renderValue()) : "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: [
            "All",
            "Today",
            "Yesterday",
            "This Week",
            "Last Week",
            "This Month",
            "Last Month",
          ],
        },
      }),
    ],
    [distinctIssuers]
  )

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    pageCount,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
  })

  const [isOpen, setIsOpen] = useState(false)
  const [reportRoutingId, setReportRoutingId] = useState(false)
  const [isNormalReport, setIsNormalReport] = useState(null)

  const showModal = (reportRoutingId, isNormalReport) => {
    setReportRoutingId(reportRoutingId)
    setIsNormalReport(isNormalReport)
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
    setReportRoutingId(null)
    setIsNormalReport(null)
  }

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                    style={{ width: `${column.getSize()}px` }}
                  >
                    <div className="flex flex-col justify-start items-start">
                      <button
                        {...{
                          className: column.column.getCanSort()
                            ? "w-full select-none cursor-pointer flex items-center gap-1"
                            : "w-full",
                          onClick: column.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          column.column.columnDef.header,
                          column.getContext()
                        )}
                        {{
                          asc: <SortAscending className="h-4 w-4" />,
                          desc: <SortDescending className="h-4 w-4" />,
                        }[column.column.getIsSorted()] ?? null}
                      </button>
                      {column.column.getCanFilter() ? (
                        <div className="mt-2">
                          <Filter column={column.column} />
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className="py-2">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 border-b border-gray-200"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No re-routed reports found</div>
        )}
      </div>
      <ReportRoutingModal
        isNormalReport={isNormalReport}
        isOpen={isOpen}
        reportRoutingId={reportRoutingId}
        onClose={onClose}
      />
    </>
  )
}

export default ReportRoutingLogsTable
